/**
 * Client-safe MoMo sub-country & operator data.
 *
 * This file is intentionally free of any server-only imports (firebase-admin,
 * firestore_server, etc.) so it can be safely imported from "use client"
 * components like PricingTable without pulling Node-only modules into the
 * browser bundle.
 *
 * The server-side momo.ts re-exports these symbols for backward compatibility.
 */

// ── Types ──────────────────────────────────────────────────────────────────

export interface NetworkInfo {
  key: string;    // API key to send to Flutterwave
  label: string;  // Display label for UI
}

export interface SubCountryConfig {
  code: string;       // ISO country code (BF, CI, SN, CM)
  name: string;       // Display name
  operators: NetworkInfo[]; // Operators available in this sub-country
}

// ── Data ───────────────────────────────────────────────────────────────────

/**
 * XOF sub-country operator configurations
 * (Burkina Faso, Côte d'Ivoire, Senegal)
 */
export const XOF_SUB_COUNTRIES: SubCountryConfig[] = [
  {
    code: "BF",
    name: "Burkina Faso",
    operators: [
      { key: "ORANGEMONEY", label: "Orange Money" },
      { key: "MOBICASH", label: "Mobicash" },
    ],
  },
  {
    code: "CI",
    name: "Côte d'Ivoire",
    operators: [
      { key: "MTN", label: "MTN" },
      { key: "ORANGEMONEY", label: "Orange Money" },
      { key: "MOOV", label: "Moov" },
      { key: "WAVE", label: "Wave" },
    ],
  },
  {
    code: "SN",
    name: "Senegal",
    operators: [
      { key: "ORANGEMONEY", label: "Orange Money" },
      { key: "WAVE", label: "Wave" },
    ],
  },
];

/**
 * XAF sub-country operator configurations
 * (Cameroon)
 */
export const XAF_SUB_COUNTRIES: SubCountryConfig[] = [
  {
    code: "CM",
    name: "Cameroon",
    operators: [
      { key: "MTN", label: "MTN" },
      { key: "ORANGE", label: "Orange" },
    ],
  },
];

// ── Francophone supported networks per ISO2 country ──────────────────────
// Exactly the network values Flutterwave accepts for mobile_money_franco.

export const FRANCO_NETWORKS_BY_COUNTRY: Record<string, string[]> = {
  CI: ["MTN", "ORANGEMONEY", "MOOV", "WAVE"],
  SN: ["ORANGEMONEY", "WAVE"],
  BF: ["ORANGEMONEY", "MOBICASH"],
  CM: ["MTN", "ORANGE"],
};

// ── XOF/XAF country calling codes ────────────────────────────────────────

/**
 * Country calling-code configs for XOF/XAF Francophone countries.
 * Used by normalizeXofPhone (phone_validation.ts) and validateXofPreflight.
 */
export const XOF_CALLING_CODES: Record<string, { callingCode: string; nationalLengths: number[] }> = {
  CI: { callingCode: "225", nationalLengths: [10] },       // Côte d'Ivoire (post-2021: 10-digit national)
  SN: { callingCode: "221", nationalLengths: [9] },        // Senegal
  BF: { callingCode: "226", nationalLengths: [8] },        // Burkina Faso
  CM: { callingCode: "237", nationalLengths: [8, 9] },     // Cameroon
};

// ── Country-name → ISO2 normalizer ────────────────────────────────────────

const FRANCO_COUNTRY_NAME_TO_ISO2: Record<string, string> = {
  "cote divoire": "CI",
  "côte divoire": "CI",
  "cote d ivoire": "CI",
  "ivory coast": "CI",
  "ivorycoast": "CI",
  "ci": "CI",
  "senegal": "SN",
  "sn": "SN",
  "burkina faso": "BF",
  "burkinafaso": "BF",
  "burkina": "BF",
  "bf": "BF",
  "cameroon": "CM",
  "cameroun": "CM",
  "cm": "CM",
};

/**
 * Normalize a country name or code to its ISO 3166-1 alpha-2 code
 * for Francophone MoMo countries (CI, SN, BF, CM).
 * Returns the uppercased input unchanged if not a known Francophone name.
 */
export function normalizeFrancoCountryToIso2(input: string): string {
  if (!input) return input;
  // Strip accents, remove punctuation that isn't a space, lowercase
  const cleaned = input
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // strip combining diacritics
    .replace(/[^a-zA-Z0-9\s]/g, "") // remove punctuation
    .trim()
    .toLowerCase();
  return FRANCO_COUNTRY_NAME_TO_ISO2[cleaned] || input.trim().toUpperCase();
}

/**
 * Validate that a network is supported for a given Francophone ISO2 country.
 * Returns the canonical uppercase network string if valid, or null if invalid.
 */
export function validateFrancoNetwork(
  iso2Country: string,
  network: string
): { valid: boolean; canonical: string | null; supported: string[] } {
  const country = iso2Country.toUpperCase();
  const supported = FRANCO_NETWORKS_BY_COUNTRY[country];
  if (!supported) {
    return { valid: false, canonical: null, supported: [] };
  }
  const upper = network.trim().toUpperCase();
  // Also accept common aliases
  const ALIASES: Record<string, string> = {
    "ORANGE": "ORANGEMONEY",
    "ORANGE_MONEY": "ORANGEMONEY",
    "ORANGE MONEY": "ORANGEMONEY",
    "FREE_MONEY": "FREE_MONEY",
    "E_MONEY": "E_MONEY",
  };
  const canonical = ALIASES[upper] || upper;
  if (supported.includes(canonical)) {
    return { valid: true, canonical, supported };
  }
  return { valid: false, canonical: null, supported };
}

// ── Helpers ────────────────────────────────────────────────────────────────

/**
 * Get sub-countries for a regional MoMo rail (XOF or XAF).
 * Returns empty array for non-regional codes.
 */
export function getSubCountries(regionCode: string): SubCountryConfig[] {
  const code = regionCode.toUpperCase();
  if (code === "XOF") return XOF_SUB_COUNTRIES;
  if (code === "XAF") return XAF_SUB_COUNTRIES;
  return [];
}

/**
 * Get operators for a sub-country within a regional rail.
 */
export function getSubCountryOperators(regionCode: string, subCountryCode: string): NetworkInfo[] {
  const subCountries = getSubCountries(regionCode);
  const sub = subCountries.find(s => s.code === subCountryCode.toUpperCase());
  return sub?.operators ?? [];
}

// ── XOF preflight validator ──────────────────────────────────────────────

/**
 * Preflight validation for XOF/XAF mobile_money_franco charges.
 * Validates network and phone number before calling the Flutterwave API.
 *
 * @param iso2Country ISO2 country code (CI, SN, BF, CM)
 * @param network Mobile money network to validate
 * @param phoneDigits Digits-only phone number (after normalizeXofPhone)
 * @returns Validation result with optional user-facing error and warnings
 */
export function validateXofPreflight(
  iso2Country: string,
  network: string,
  phoneDigits: string
): { valid: boolean; error?: string; warnings: string[] } {
  const upper = iso2Country.toUpperCase();
  const warnings: string[] = [];

  // 1. Validate country is known
  const supported = FRANCO_NETWORKS_BY_COUNTRY[upper];
  if (!supported) {
    return {
      valid: false,
      error: `Country "${iso2Country}" is not supported for Francophone Mobile Money. Supported: CI, SN, BF, CM.`,
      warnings,
    };
  }

  // 2. Validate network
  if (!network) {
    return {
      valid: false,
      error: "Please select your mobile money provider (e.g., MTN, Orange Money, Wave).",
      warnings,
    };
  }

  const nv = validateFrancoNetwork(upper, network);
  if (!nv.valid) {
    return {
      valid: false,
      error: `${network} is not available in ${upper}. Please choose: ${nv.supported.join(", ")}.`,
      warnings,
    };
  }

  // 3. Validate phone number format
  const ccConfig = XOF_CALLING_CODES[upper];
  if (ccConfig) {
    if (!phoneDigits || !phoneDigits.startsWith(ccConfig.callingCode)) {
      return {
        valid: false,
        error: `Phone number must start with country code ${ccConfig.callingCode} for ${upper}. ` +
          `Please enter your full mobile money phone number (e.g., +${ccConfig.callingCode}...).`,
        warnings,
      };
    }
    const nationalPart = phoneDigits.slice(ccConfig.callingCode.length);
    if (!ccConfig.nationalLengths.includes(nationalPart.length)) {
      warnings.push(
        `Phone national part is ${nationalPart.length} digits (expected ${ccConfig.nationalLengths.join(" or ")} for ${upper}). ` +
        `If the charge fails with PAYER_NOT_FOUND, verify the phone matches your ${network} wallet.`
      );
    }
  }

  return { valid: true, warnings };
}
